// Team Schedule year filter: which calendar years a job row touches, the
// year options to offer, and the match a row has to pass.
//
// A row belongs to every year its start..end span touches, not only the year
// it started. So a job that began in December 2025 and finishes in April 2026
// is listed under 2025 and under 2026, while a job that finished in 2025 drops
// out of the 2026 view instead of being deleted. Rows carry moment dates
// (`start` always real, `end` real or null, see teamGantt.js allRows).

export const ALL = 'ALL';

// A span longer than this is a sentinel date (2099, 9999), not a plan; the
// years past the cap are not offered so the Year list stays readable.
export const MAX_SPAN_YEARS = 10;

export const rowYears = (row) => {
    const s = row && row.start && typeof row.start.year === 'function' ? row.start.year() : null;
    if (s == null) return [];
    const e = row.end && typeof row.end.year === 'function' ? row.end.year() : s;
    const last = Math.min(Math.max(s, e), s + MAX_SPAN_YEARS);
    const years = [];
    for (let y = s; y <= last; y++) years.push(y);
    return years;
};

const byName = (a, b) => String(a).localeCompare(String(b));

// Whether a burn-tool labor interval may stretch the timeline window: only
// when it lies within `years` of the job's own contract span. Labor can be
// planned a little outside the contract dates and should stay on screen, but
// a window dated 2010 on a 2026 job is bad data and must not drag the
// timeline back sixteen years. Such an interval still draws, clipped at the
// edge of the window.
const isMoment = (m) => !!m && typeof m.clone === 'function' && typeof m.isBefore === 'function';

export const intervalNearSpan = (row, iv, years = 1) => {
    if (!row || !isMoment(row.start) || !iv || !isMoment(iv.start) || !isMoment(iv.end)) return false;
    const lo = row.start.clone().subtract(years, 'year');
    const hi = (row.end || row.start).clone().add(years, 'year');
    return !iv.start.isBefore(lo) && !iv.end.isAfter(hi);
};

export const matchesYear = (row, year) =>
    year === ALL || year == null || year === '' || rowYears(row).includes(Number(year));

// Newest first, as strings (the <select> hands back strings). The current year
// is always offered so the default view exists before any job touches it.
export const yearOptions = (rows, currentYear) => {
    const set = new Set();
    if (currentYear != null && Number.isFinite(Number(currentYear))) set.add(Number(currentYear));
    (rows || []).forEach((r) => rowYears(r).forEach((y) => set.add(y)));
    return Array.from(set).sort((a, b) => b - a).map(String);
};

// Picked values for a checkbox filter (Team, PM, Super): an empty list means
// everything. More than one can be picked, so two teams' schedules can be
// compared in one view, or two PMs' jobs seen together.
export const matchesPicked = (value, picked) => !picked || picked.length === 0 || picked.includes(value);

// The picker button's text: "All <noun>", up to maxNamed values in name order,
// else a count. Teams name two (short codes); people name one (long names).
export const pickedLabel = (picked, noun, maxNamed = 1) => {
    if (!picked || picked.length === 0) return `All ${noun}`;
    if (picked.length <= maxNamed) return picked.slice().sort(byName).join(', ');
    return `${picked.length} ${noun}`;
};

export const matchesTeams = (row, teams) => matchesPicked(row.team, teams);
export const teamLabel = (teams) => pickedLabel(teams, 'teams', 2);

const sumCv = (rows) => rows.reduce((s, r) => s + (Number(r.cv) || 0), 0);

// PM groups in PM order, each group's jobs in start order (what the page
// always showed).
export const groupByPm = (rows) => {
    const byPm = {};
    (rows || []).forEach((r) => { (byPm[r.pm] = byPm[r.pm] || []).push(r); });
    return Object.keys(byPm).sort(byName).map((pm) => {
        const items = byPm[pm].slice().sort((a, b) => a.start.valueOf() - b.start.valueOf());
        return { pm, items, totalCv: sumCv(items) };
    });
};

// Sections to draw: one band per picked team when two or more are picked,
// else a single unnamed section (team null) holding the PM groups as before.
// A picked team with no matching jobs still gets its band, so the comparison
// says "nothing here" instead of silently leaving the team out.
export const sectionByTeam = (rows, teams) => {
    const all = rows || [];
    if (!teams || teams.length < 2) {
        return [{ team: null, groups: groupByPm(all), count: all.length, totalCv: sumCv(all) }];
    }
    return teams.slice().sort(byName).map((team) => {
        const own = all.filter((r) => r.team === team);
        return { team, groups: groupByPm(own), count: own.length, totalCv: sumCv(own) };
    });
};
